import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'

interface ButtonProps {
  children: ReactNode
  to?: string
  href?: string
  variant?: 'primary' | 'outline' | 'outlineWhite'
  size?: 'md' | 'lg'
  className?: string
  type?: 'button' | 'submit'
  disabled?: boolean
  onClick?: () => void
}

const variants = {
  primary:
    'bg-primary text-white hover:bg-primary-hover hover:scale-105 active:scale-100',
  outline:
    'border-2 border-primary text-primary hover:bg-primary hover:text-white',
  outlineWhite:
    'border-2 border-white text-white hover:bg-white hover:text-secondary',
}

const sizes = {
  md: 'px-6 py-2.5 text-sm',
  lg: 'px-8 py-3.5 text-base',
}

export default function Button({
  children,
  to,
  href,
  variant = 'primary',
  size = 'md',
  className = '',
  type = 'button',
  disabled,
  onClick,
}: ButtonProps) {
  const classes = `inline-flex items-center justify-center font-medium rounded-btn transition-all duration-200 ${variants[variant]} ${sizes[size]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`

  // A disabled Button must never navigate, even when given a `to`/`href`.
  // Render a real disabled <button> so it cannot be clicked or focused-and-activated.
  if (disabled) {
    return (
      <button type="button" className={classes} disabled aria-disabled="true">
        {children}
      </button>
    )
  }

  if (to) {
    return (
      <Link to={to} className={classes} onClick={onClick}>
        {children}
      </Link>
    )
  }

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={classes}>
        {children}
      </a>
    )
  }

  return (
    <button type={type} className={classes} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  )
}
